import React, { useEffect, useMemo, useState, useCallback } from "react";
import { computeServiceStatus } from "./serviceSchedule";

function fmtKm(km) {
  if (km == null) return "—";
  if (Math.abs(km) >= 1000) return `${(km / 1000).toFixed(1)} k km`;
  return `${Math.round(km)} km`;
}

function fmtTime(epoch) {
  if (!epoch) return "—";
  const n = Number(epoch);
  const ms = n > 1e12 ? n : n * 1000;
  return new Date(ms).toLocaleString();
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString();
}

function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

const STATUS_LABEL = {
  ok: "OK",
  due_soon: "Due soon",
  due: "Due",
  overdue: "Overdue",
};

// MySQL only keeps the last 30 days of fault transitions (daily purge event),
// so the time-range selector caps at 30d. The mongo obd_data side retains
// full history if a deeper dive is ever needed.
const HISTORY_RANGES = [
  { value: 1,  label: "24h" },
  { value: 7,  label: "7d"  },
  { value: 30, label: "30d" },
];

export default function VehicleHistory({ vehicleId, onBack }) {
  const [v, setV]               = useState(null);
  const [history, setHistory]   = useState({ faultEvents: [], swapEvents: [] });
  const [services, setServices] = useState([]);
  const [tab, setTab]           = useState("overview");
  const [err, setErr]           = useState("");
  const [busy, setBusy]         = useState(true);
  const [markBusy, setMarkBusy] = useState(false);
  const [historyDays, setHistoryDays] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("vd_historyDays") || "30", 10);
      return HISTORY_RANGES.some((r) => r.value === v) ? v : 30;
    } catch { return 30; }
  });
  useEffect(() => {
    try { localStorage.setItem("vd_historyDays", String(historyDays)); } catch {}
  }, [historyDays]);

  // Vehicle + service log only need to load once per vehicle. Fault history
  // re-fetches whenever the time-range changes.
  const reloadBase = useCallback(async () => {
    setErr("");
    try {
      const [vr, sr] = await Promise.all([
        fetch(`/api/vehicles/${encodeURIComponent(vehicleId)}`, { credentials: "include" }),
        fetch(`/api/vehicles/${encodeURIComponent(vehicleId)}/services`, { credentials: "include" }),
      ]);
      if (!vr.ok) throw new Error(`Vehicle not found (${vr.status})`);
      const [vj, sj] = await Promise.all([vr.json(), sr.json()]);
      setV(vj); setServices(sj);
    } catch (e) {
      setErr(e.message || "Failed to load");
    }
  }, [vehicleId]);

  const reloadHistory = useCallback(async () => {
    try {
      const hr = await fetch(
        `/api/vehicles/${encodeURIComponent(vehicleId)}/history?days=${historyDays}`,
        { credentials: "include" }
      );
      if (!hr.ok) throw new Error(`History fetch failed (${hr.status})`);
      setHistory(await hr.json());
    } catch (e) {
      // Surface in the panel without clobbering the rest of the page.
      setErr(e.message || "Failed to load history");
    }
  }, [vehicleId, historyDays]);

  const reload = useCallback(async () => {
    setBusy(true);
    await Promise.all([reloadBase(), reloadHistory()]);
    setBusy(false);
  }, [reloadBase, reloadHistory]);

  useEffect(() => { reload(); /* initial */ }, [vehicleId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reloadHistory(); }, [historyDays, reloadHistory]);

  const odo = v ? Number(v.odometer || 0) : 0;
  const serviceStatus = useMemo(
    () => computeServiceStatus(odo, services),
    [odo, services]
  );

  const overdueCount = serviceStatus.filter((s) => s.status === "overdue").length;
  const dueCount     = serviceStatus.filter((s) => s.status === "due").length;

  async function markDone(svc) {
    if (markBusy) return;
    const notes = window.prompt(
      `Mark "${svc.item}" as done at ${Math.round(odo)} km. Add notes (optional):`,
      ""
    );
    if (notes === null) return; // user cancelled
    setMarkBusy(true);
    try {
      const res = await fetch(
        `/api/vehicles/${encodeURIComponent(vehicleId)}/services/mark-done`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            system: svc.system,
            item: svc.item,
            done_at_km: odo,
            done_at_date: new Date().toISOString().slice(0, 10),
            notes: notes || null,
          }),
        }
      );
      if (!res.ok) throw new Error(`Mark-done failed (${res.status})`);
      await reloadBase();
    } catch (e) {
      alert(e.message);
    } finally {
      setMarkBusy(false);
    }
  }

  if (busy && !v)
    return <div className="vh-page"><div className="loading">Loading vehicle history…</div></div>;

  if (err && !v)
    return (
      <div className="vh-page">
        <div className="vh-top">
          <button className="vh-back" onClick={onBack}>← Back</button>
        </div>
        <div className="banner error">⚠ {err}</div>
      </div>
    );

  return (
    <div className="vh-page">
      <div className="vh-top">
        <button className="vh-back" onClick={onBack}>← Back to fleet</button>
        <div className="vh-title">
          <h1>{v.vehicle_id}</h1>
          <div className="vh-subtitle">
            {v.company_name && <span>{v.company_name}</span>}
            {v.sb_vehicle_id && <span>SB: {v.sb_vehicle_id}</span>}
            {v.model && <span>{v.model}</span>}
            <span>· {fmtKm(odo)} on the clock</span>
          </div>
          {(v.battery_id || v.enneagon_plate_no) && (
            <div className="vh-bat">
              {v.battery_id && <span>BMS ID: <b>{v.battery_id}</b></span>}
              {v.enneagon_plate_no && <span>Battery Serial: <b>{v.enneagon_plate_no}</b></span>}
              {v.bcs_capacity && <span>{v.bcs_capacity}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="vh-tabs">
        <button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")}>Overview</button>
        <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>
          Vehicle History
          {history.faultEvents.length > 0 && (
            <span className="vh-tab-count">{history.faultEvents.length}</span>
          )}
        </button>
        <button className={tab === "service" ? "active" : ""} onClick={() => setTab("service")}>
          Service
          {(overdueCount + dueCount) > 0 && (
            <span className={`vh-tab-count ${overdueCount > 0 ? "danger" : "warn"}`}>
              {overdueCount + dueCount}
            </span>
          )}
        </button>
      </div>

      {tab === "overview"  && <OverviewTab v={v} serviceStatus={serviceStatus} />}
      {tab === "history"  && (
        <HistoryTab
          history={history}
          days={historyDays}
          onDaysChange={setHistoryDays}
        />
      )}
      {tab === "service"  && <ServiceTab services={serviceStatus} log={services} odo={odo} markDone={markDone} markBusy={markBusy} />}
    </div>
  );
}

// ── Overview tab ────────────────────────────────────────────────────────────

function OverviewTab({ v, serviceStatus }) {
  const overdue = serviceStatus.filter((s) => s.status === "overdue");
  const due     = serviceStatus.filter((s) => s.status === "due");

  return (
    <div className="vh-body">
      <div className="vh-grid">
        <div className="vh-card">
          <div className="vh-card-title">Current State</div>
          <Row label="Odometer"        v={fmtKm(v.odometer)} />
          <Row label="SOC"             v={v.soc != null ? `${Number(v.soc).toFixed(1)}%` : "—"} />
          <Row label="Engine"          v={v.engine === "True" ? "ON" : "OFF"} />
          <Row label="Vehicle State"   v={v.vehicle_state} />
          <Row label="Speed"           v={v.wheel_speed != null ? `${Number(v.wheel_speed).toFixed(0)} km/h` : "—"} />
          <Row label="OBD last seen"   v={fmtTime(v.obd_end_time)} />
          <Row label="GPS last seen"   v={fmtTime(v.gps_time)} />
        </div>

        <div className="vh-card">
          <div className="vh-card-title">Battery (current)</div>
          <Row label="BMS ID"                v={v.battery_id || "—"} mono />
          <Row label="Battery Serial Number" v={v.enneagon_plate_no || "—"} mono />
          <Row label="BCS Capacity"          v={v.bcs_capacity || "—"} />
          <Row label="Pack Voltage"          v={v.batt_volt != null ? `${Number(v.batt_volt).toFixed(1)} V` : "—"} />
          <Row label="Pack Current"          v={v.batt_cur != null ? `${Number(v.batt_cur).toFixed(1)} A` : "—"} />
          <Row label="Max Cell Temp"         v={v.max_cell_temp != null ? `${Number(v.max_cell_temp).toFixed(0)} °C` : "—"} />
          <Row label="Total Discharge"       v={v.total_dischg_pwr != null ? `${Number(v.total_dischg_pwr).toFixed(0)} kWh` : "—"} />
        </div>

        <div className="vh-card">
          <div className="vh-card-title">Faults (current)</div>
          <Row label="VCU"             v={`Level ${v.vcu_fault_level ?? 0}`} />
          <Row label="VCU Strategy"    v={`Level ${v.vcu_strategy_fault_level ?? 0}`} />
          <Row label="MCU"             v={`Level ${v.mcu_error_level ?? 0}`} />
          <Row label="Motor"           v={v.motor_fault === 1 ? "FAULT" : "OK"} />
        </div>

        <div className="vh-card">
          <div className="vh-card-title">Service Summary</div>
          <Row label="Overdue items"   v={overdue.length} />
          <Row label="Due now"         v={due.length} />
          <Row label="Total tracked"   v={serviceStatus.length} />
          {overdue.length === 0 && due.length === 0 ? (
            <div className="vh-allgood">All services up to date.</div>
          ) : (
            <div className="vh-mini-list">
              {[...overdue, ...due].slice(0, 5).map((s, i) => (
                <div key={i} className={`vh-mini-row status-${s.status}`}>
                  <span className={`vh-pill ${s.status}`}>{STATUS_LABEL[s.status]}</span>
                  <span className="vh-mini-item">{s.item}</span>
                  <span className="vh-mini-km">{fmtKm(Math.abs(s.km_to_due))}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, v, mono }) {
  return (
    <div className="vh-row">
      <span className="vh-row-label">{label}</span>
      <span className={`vh-row-value ${mono ? "mono" : ""}`}>{v}</span>
    </div>
  );
}

// ── History tab ─────────────────────────────────────────────────────────────
// Pure fault timeline: every transition captured by the
// trg_vehicle_status_fault_log trigger. Newest first.

function HistoryTab({ history, days, onDaysChange }) {
  const events = history.faultEvents || [];
  const rangeLabel =
    HISTORY_RANGES.find((r) => r.value === days)?.label || `${days}d`;

  return (
    <div className="vh-body">
      <div className="vh-history-bar">
        <div className="vh-range-group" role="group" aria-label="Time range">
          {HISTORY_RANGES.map((r) => (
            <button
              key={r.value}
              className={r.value === days ? "active" : ""}
              onClick={() => onDaysChange(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <span className="vh-history-count">
          {events.length} {events.length === 1 ? "event" : "events"} · last {rangeLabel}
        </span>
      </div>
      {events.length === 0 ? (
        <div className="vh-empty">No fault transitions in the last {rangeLabel}.</div>
      ) : (
        <div className="vh-timeline">
          {events.map((e, i) => <FaultRow key={i} e={e} />)}
        </div>
      )}
    </div>
  );
}

function FaultRow({ e }) {
  const lvl  = Number(e.level || 0);
  const prev = e.prev_level == null ? null : Number(e.prev_level);

  // Ignition events render differently — they describe a binary state change,
  // not a severity level.
  if (e.system === "Ignition") {
    const on = lvl === 1;
    return (
      <div className="vh-tl-row ignition">
        <span className="vh-tl-time">{fmtDateTime(e.created_at)}</span>
        <span className="vh-pill ignition">{on ? "IGN ON" : "IGN OFF"}</span>
        <span className="vh-tl-msg">
          Ignition turned <b>{on ? "ON" : "OFF"}</b>
        </span>
        <span className="vh-tl-extra">{e.odometer != null ? fmtKm(Number(e.odometer)) : ""}</span>
      </div>
    );
  }

  const cls  = lvl >= 2 ? "critical" : lvl >= 1 ? "warning" : "info";

  // Describe the transition in plain words.
  let verb;
  if (prev == null || prev === 0) {
    verb = lvl > 0 ? "triggered" : "cleared";
  } else if (lvl === 0) {
    verb = "cleared";
  } else if (lvl > prev) {
    verb = "escalated";
  } else if (lvl < prev) {
    verb = "downgraded";
  } else {
    verb = "re-emitted";
  }

  return (
    <div className={`vh-tl-row ${cls}`}>
      <span className="vh-tl-time">{fmtDateTime(e.created_at)}</span>
      <span className={`vh-pill ${cls}`}>{e.system}</span>
      <span className="vh-tl-msg">
        <b>{verb}</b>
        {" · "}
        {prev != null ? `Level ${prev}` : "—"} → <b>Level {lvl}</b>
      </span>
      <span className="vh-tl-extra">{e.odometer != null ? fmtKm(Number(e.odometer)) : ""}</span>
    </div>
  );
}

// ── Service tab ─────────────────────────────────────────────────────────────

function ServiceTab({ services, log, odo, markDone, markBusy }) {
  // Group by system
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of services) {
      if (!map.has(s.system)) map.set(s.system, []);
      map.get(s.system).push(s);
    }
    return Array.from(map.entries());
  }, [services]);

  return (
    <div className="vh-body">
      <div className="vh-service-summary">
        <span><b>Current odometer:</b> {fmtKm(odo)}</span>
        <span>·</span>
        <span><b>{services.filter((s) => s.status === "overdue").length}</b> overdue</span>
        <span><b>{services.filter((s) => s.status === "due").length}</b> due</span>
        <span><b>{services.filter((s) => s.status === "due_soon").length}</b> due soon</span>
        <span><b>{services.filter((s) => s.status === "ok").length}</b> ok</span>
      </div>

      {groups.map(([system, items]) => (
        <div key={system} className="vh-service-group">
          <div className="vh-service-group-title">{system}</div>
          <table className="vh-service-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Action</th>
                <th>Period</th>
                <th>Last done</th>
                <th>Next due</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s, i) => (
                <tr key={i} className={`status-${s.status}`}>
                  <td>{s.item}</td>
                  <td><span className="vh-action">{s.action}</span> {s.action_label}</td>
                  <td>{fmtKm(s.period_km)}</td>
                  <td>{s.last_done_km != null ? fmtKm(s.last_done_km) : <span className="muted">never</span>}</td>
                  <td>{fmtKm(s.next_due_km)}</td>
                  <td>
                    <span className={`vh-pill ${s.status}`}>
                      {STATUS_LABEL[s.status]}
                      {s.status !== "ok" && ` · ${fmtKm(Math.abs(s.km_to_due))}`}
                    </span>
                  </td>
                  <td>
                    <button
                      className="vh-mark-done"
                      disabled={markBusy}
                      onClick={() => markDone(s)}
                    >
                      Mark Done
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {log.length > 0 && (
        <div className="vh-service-group">
          <div className="vh-service-group-title">Recent completions</div>
          <table className="vh-service-table compact">
            <thead>
              <tr>
                <th>When</th>
                <th>Item</th>
                <th>System</th>
                <th>At odometer</th>
                <th>Marked by</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {log.slice(0, 30).map((l) => (
                <tr key={l.id}>
                  <td>{fmtDate(l.done_at_date)}</td>
                  <td>{l.item}</td>
                  <td>{l.system}</td>
                  <td>{fmtKm(l.done_at_km)}</td>
                  <td className="small">{l.marked_by || "—"}</td>
                  <td className="small">{l.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
